// pages/SubscriptionPlanScreen.tsx
import React from 'react';
import { FaInfo } from 'react-icons/fa';
import Button from '../components/Button';
import ConfirmationDialog from '../components/ConfirmationDialog';
import DataTableView from '../components/DataTableView2';
import Select from '../components/Select';
import AddSubscriptionPlanModule from '../components/SubscriptionPlans/AddSubscriptionPlanModal';
import EditSubscriptionPlanModule from '../components/SubscriptionPlans/EditSubscriptionPlanModal';
import ViewSubscriptionPlan from '../components/SubscriptionPlans/ViewSubscriptionPlan';
import Toggle from '../components/Toggle';
import { subscriptionStatusOptions } from '../utils/filters';
import { capitalize } from '../utils/helpers';
import { IGetSubscriptionPlans } from '../utils/types';
import useSubscriptionPlans from '../hooks/logics/subscription-plan/use-subscription-plan-dashboard';

const SubscriptionPlanScreen = () => {
  const {
    subscriptionPlans,
    totalPages,
    isFetching,
    planTypes,
    currentPage,
    setCurrentPage,
    isActive,
    setIsActive,
    subscriptionPlan,
    setSubscriptionPlan,
    selectedSubscriptionId,
    setSelectedSubscriptionId,
    subscriptionStatusChangeId,
    isViewSubscriptionPlanVisible,
    setIsViewSubscriptionPlanVisible,
    isAddModalVisible,
    setIsAddModalVisible,
    isFreePlanModalVisible,
    setIsFreePlanModalVisible,
    isEditMode,
    setIsEditMode,
    isCancellingSubscription,
    isAnyFilterApplied,
    applyFilters,
    clearFilters,
    handleCancelSubscription,
    handleSubscriptionStatusChanged,
    refetch,
  } = useSubscriptionPlans();

  const columns = [
    {
      header: 'Name',
      className: 'md:p-0 md:pl-10',
      accessor: (row: IGetSubscriptionPlans) => (
        <div className='flex items-center gap-2 md:!pl-7'>
          <div
            style={{ backgroundColor: row.colorCode }}
            className='w-4 h-4 rounded-full'
          />
          <span>{row.planName || 'N/A'}</span>
        </div>
      ),
    },
    {
      header: 'Price',
      accessor: (row: IGetSubscriptionPlans) =>
        row?.price > 0 ? `$ ${row.price}` : 'Free',
    },
    {
      header: 'Deliveries',
      accessor: (row: IGetSubscriptionPlans) => row.deliveries || 'N/A',
    },
    {
      header: 'Type',
      accessor: (row: IGetSubscriptionPlans) =>
        capitalize(row.planType) || 'N/A',
    },
    {
      header: 'Actions',
      accessor: (row: IGetSubscriptionPlans) => (
        <div className='flex flex-row items-center space-x-2 ml-2'>
          <Toggle
            labelClassName='mr-2'
            label='Active'
            disabled={subscriptionStatusChangeId === row._id}
            checked={row.active === true}
            onChange={() =>
              handleSubscriptionStatusChanged(row._id, row.active)
            }
          />
          <Button
            onClick={() => {
              setSubscriptionPlan(row);
              setIsEditMode(true);
            }}
            className='text-primary w-max p-0'
            variant='ghost'
          >
            Edit
          </Button>
          <Button
            variant='ghost'
            className='text-red-600 w-max p-0'
            onClick={() => setSelectedSubscriptionId(row._id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
    {
      header: 'Info',
      accessor: (row: IGetSubscriptionPlans) => (
        <div className='mt-0.5 md:mt-0'>
          <Button
            onClick={() => {
              setSubscriptionPlan(row);
              setIsViewSubscriptionPlanVisible(true);
            }}
            className='md:p-2 p-1 rounded-full'
          >
            <FaInfo className='w-3 h-3' />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className='w-full h-full'>
        <div className='pb-4 flex flex-col justify-center font-inter bg-gray-100'>
          <div className='flex items-center justify-end mb-2 md:mb-3'>
            <div className='w-full flex flex-col md:flex-row gap-4'>
              <div className='w-full flex flex-row gap-x-2 md:gap-x-4 md:justify-end'>
                <Select
                  containerClassName='w-1/2 md:w-auto'
                  className='md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2'
                  options={subscriptionStatusOptions}
                  placeholder='Status'
                  onChange={(e) => setIsActive(e.target.value)}
                  value={isActive}
                />
                <Button
                  disabled={!isAnyFilterApplied}
                  className='flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10'
                  onClick={applyFilters}
                >
                  Apply
                </Button>
              </div>
              <div className='w-full md:w-auto flex gap-x-2 md:gap-x-4 mb-8 md:mb-0'>
                <Button
                  disabled={!isAnyFilterApplied}
                  onClick={clearFilters}
                  type='button'
                  className='flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10'
                >
                  Clear
                </Button>
                <Button
                  onClick={() => setIsAddModalVisible(true)}
                  className='flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10'
                >
                  Add plan
                </Button>
                <Button
                  onClick={() => setIsFreePlanModalVisible(true)}
                  className='flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10'
                  variant='outline'
                >
                  Free plan
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DataTableView
          isFetching={isFetching}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          currentPage={currentPage}
          emptyStateMessage='No data found'
          columns={columns}
          data={subscriptionPlans}
          height=''
        />

        <ViewSubscriptionPlan
          isVisible={isViewSubscriptionPlanVisible}
          onHide={() => setIsViewSubscriptionPlanVisible(false)}
          subscriptionPlan={subscriptionPlan}
        />
        <AddSubscriptionPlanModule
          isVisible={isAddModalVisible}
          onHide={() => setIsAddModalVisible(false)}
          onSuccess={refetch}
          planTypes={planTypes}
        />
        <AddSubscriptionPlanModule
          isVisible={isFreePlanModalVisible}
          onHide={() => setIsFreePlanModalVisible(false)}
          onSuccess={refetch}
          planTypes={planTypes}
          isFreePlan={true}
        />
        <EditSubscriptionPlanModule
          isVisible={isEditMode}
          onHide={() => setIsEditMode(false)}
          onSuccess={refetch}
          planTypes={planTypes}
          data={subscriptionPlan}
        />
        <ConfirmationDialog
          isSubmitButtonDisabled={isCancellingSubscription}
          isSubmitting={isCancellingSubscription}
          handleClick={handleCancelSubscription}
          isVisible={selectedSubscriptionId !== ''}
          onHide={() => setSelectedSubscriptionId('')}
          text='Are you sure you want to delete this subscription?'
        />
      </div>
    </>
  );
};

export default SubscriptionPlanScreen;
